import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import TeaserImages from '../Helpers/TeaserImages';
import RelatedItemsDisplay from '../Helpers/RelatedItemsDisplay';
import {
  RESEARCHER_POSITIONS,
  filterByPosition,
} from '../Helpers/positionFilters';
import { hasImage, vocabTitle } from '../../../helpers/content';
import { flattenRichTextToAppURL } from '../../../helpers/richText';

const TopicView = (props) => {
  const { content } = props;
  const teaserImage1 = content.teaser_image1;
  const teaserImage2 = content.teaser_image2;

  const topicTypeLabel = vocabTitle(content.topic_type) || null;

  const hasImages = hasImage(teaserImage1) || hasImage(teaserImage2);

  // `topic_description` is the field the schema offers and the edit form
  // shows; `abstract` is what it used to be called. Reading only the old name
  // meant the nine thesis topics, which have the new field filled, rendered
  // nothing at all.
  const topicDescription = content.topic_description;
  const hasAbstract =
    topicDescription?.data && topicDescription.data !== '<p></p>';

  const researchers = filterByPosition(
    content.related_researchers,
    RESEARCHER_POSITIONS,
  );
  const hasResearchers =
    !content.hide_related_researchers && researchers.length > 0;
  const hasRelations = hasResearchers;

  return (
    <Container id="view-wrapper" className="TopicView">
      {/* Header */}
      <div className="view-header">
        {topicTypeLabel && (
          <p className="view-header__subtitle">{topicTypeLabel}</p>
        )}
        <h2 className="view-header__title">{content.title || content.id}</h2>
        {/* No description here. `description` is a listing field - the one
            line that goes on a card and into a search result - and repeating
            it as the first paragraph of the page it summarises says the same
            thing twice. The full view has its own content. */}
        <div className="view-header__divider" />
      </div>
      <Sections>
        {/* Teaser Images */}
        {hasImages && <TeaserImages content={content} />}

        {/* Abstract */}
        {hasAbstract && (
          <>
            <div className="view-section">
              <div
                className="view-section__richtext"
                dangerouslySetInnerHTML={{
                  __html: flattenRichTextToAppURL(topicDescription.data),
                }}
              />
            </div>
          </>
        )}

        {/* Related Items: Associated Researchers is the authoritative list of
            staff working in this area - see .claude/claude.md "Owning Side"-
            Regel, section 7d. Projects/Publications are intentionally NOT
            shown here anymore (removed 2026-08-03) - use a Listing/Search
            Block with "Project Topics (linked)" / "Publication Related Topics
            (linked)" instead, since Project/Publication already own their
            related_topics field. */}
        {hasRelations && (
          <>
            {hasResearchers && (
              <div className="view-section">
                <h3 className="view-section__label">Associated Researchers:</h3>
                <RelatedItemsDisplay
                  items={researchers}
                  mode={content.related_researchers_display}
                  kind="person"
                  fieldKey={`${content['@id']}-related_researchers`}
                />
              </div>
            )}
          </>
        )}
      </Sections>

      {/* The block-rendered part of the page */}
      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

TopicView.propTypes = {
  content: PropTypes.shape({
    '@id': PropTypes.string,
    title: PropTypes.string,
    id: PropTypes.string,
    description: PropTypes.string,
    topic_type: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ title: PropTypes.string }),
    ]),
    topic_description: PropTypes.shape({ data: PropTypes.string }),
    teaser_image1: PropTypes.object,
    teaser_image1_caption: PropTypes.string,
    teaser_image1_copyright: PropTypes.string,
    teaser_image2: PropTypes.object,
    teaser_image2_caption: PropTypes.string,
    teaser_image2_copyright: PropTypes.string,
    hide_related_researchers: PropTypes.bool,
    related_researchers_display: PropTypes.string,
    related_researchers: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        review_state: PropTypes.string,
        is_linkable: PropTypes.bool,
        full_name: PropTypes.string,
        title: PropTypes.string,
        type_of_position: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({ title: PropTypes.string, token: PropTypes.string }),
        ]),
      }),
    ),
  }).isRequired,
};

export default TopicView;
